import React, { useCallback, useEffect, useState } from 'react';
import { useOnboardingStore } from '../../store';
import { ArrowLeft, Check, FastForward, Lock, X } from 'lucide-react';
import { canSkipSteps } from '../../config/beta';
import { isDeveloperMode } from '../../config/devMode';
import { supabase, supabaseConfig } from '../../lib/supabase';
import {
  loadServiceSubscriptionAvailability,
  recordServiceAvailabilityInterest,
  serviceIsUnavailable,
  serviceUnavailableMessage,
  type ServiceSubscriptionAvailability,
} from '../../lib/serviceAvailability';

interface ServiceOption {
  id: string;
  name: string;
  serviceType: string;
  initialCost?: number;
  monthlyFee: number;
  description: string;
  features: string[];
}

interface ServiceCapacity {
  service_slug: string;
  service_name: string;
  client_limit: number;
  limit_enabled: boolean;
  is_at_capacity: boolean;
}

type LoadState = 'loading' | 'ready' | 'error';

const CAPACITY_MESSAGE = 'Scale Small AI is temporarily not accepting new subscribers for this service while the client limit is protected. Clients can request a notification when new subscriptions reopen.';

const CAPACITY_SERVICE_SLUGS: Record<string, string> = {
  'content-engine': 'content_engine',
  'proof-of-work': 'jobs_to_socials'
};
const AI_AUDIT_OPTION_ID = 'ai-audit-monthly';
const SEO_GBP_AUDIT_OPTION_ID = 'seo-gbp-audit-monthly';
const PAIRED_AUDIT_PRICE = 50;
const PAIRED_AUDIT_DISCOUNT = 10;
const REPEAT_REFERRAL_SERVICE_TYPE = 'repeat-referral-system';
const CUSTOMER_INTELLIGENCE_SERVICE_TYPE = 'customer-intelligence';

const SERVICE_OPTIONS: ServiceOption[] = [
  {
    id: 'repeat-referral-monthly',
    name: 'Repeat & Referral System - Monthly',
    serviceType: 'repeat-referral-system',
    monthlyFee: 149,
    description: 'Customer Moment Engine - Monthly billing',
    features: [
      'Timing-based customer outreach queue',
      'Referral request timing and approval routing',
      'Win-back campaigns for lapsed customers',
      'Built-in suppression rules',
      'Loyalty moment triggers',
      'Best for service businesses that rely on repeat customers'
    ]
  },
  {
    id: 'repeat-referral-annual',
    name: 'Repeat & Referral System - Annual',
    serviceType: 'repeat-referral-system',
    monthlyFee: 124,
    description: 'Customer Moment Engine - Annual billing (2 months free)',
    features: [
      'Timing-based customer outreach queue',
      'Referral request timing and approval routing',
      'Win-back campaigns for lapsed customers',
      'Built-in suppression rules',
      'Loyalty moment triggers',
      'Save $300/year with annual billing'
    ]
  },
  {
    id: 'content-engine-monthly',
    name: 'Content Engine - Monthly',
    serviceType: 'content-engine',
    monthlyFee: 499,
    description: 'Website Articles + Short-Form Video Packages - Monthly billing',
    features: [
      'Website articles produced from service, area, and seasonal priorities',
      'Short-form video packages prepared for approved channels',
      'Batched website publishing',
      'Configured release rules for website publishing and approved social channels',
      'Brand voice consistency',
      'Manual upload and approved assets can enrich output'
    ]
  },
  {
    id: 'proof-of-work-monthly',
    name: 'Proof-of-Work Auto Publisher - Monthly',
    serviceType: 'proof-of-work',
    monthlyFee: 250,
    description: 'Job Photos to Queued or Approved Posts - Monthly billing',
    features: [
      'Job photos become queued or approved posts',
      'Location context applied automatically',
      'Batched scheduling to avoid flooding',
      'No manual captions needed',
      'Perfect for field service businesses',
      'Showcases completed work consistently'
    ]
  },
  {
    id: 'proof-of-work-annual',
    name: 'Proof-of-Work Auto Publisher - Annual',
    serviceType: 'proof-of-work',
    monthlyFee: 208,
    description: 'Job Photos to Queued or Approved Posts - Annual billing',
    features: [
      'Job photos become queued or approved posts',
      'Location context applied automatically',
      'Batched scheduling to avoid flooding',
      'No manual captions needed',
      'Perfect for field service businesses',
      'Save $504/year with annual billing'
    ]
  },
  {
    id: 'local-authority-engine',
    name: 'Local Authority Engine',
    serviceType: 'nap-entity-boost',
    monthlyFee: 199,
    description: 'Connected-source audit, NAP consistency, Website Schema.org evidence, eligible remediation, verification checks, and owner-action routing. Exact tier is selected later in the dashboard before payment.',
    features: [
      'Full local authority scan after setup',
      'NAP issue detection with eligible remediation where access allows',
      'Google Business Profile and website schema/entity checks',
      'Activated connector, provider-path, and owner-action routing',
      'Step-by-step client instructions for publisher actions that cannot be automated',
      'Tier level and billing cycle are selected later in the dashboard before payment'
    ]
  },
  {
    id: AI_AUDIT_OPTION_ID,
    name: 'AI Audit - Monthly',
    serviceType: 'ai-audit',
    monthlyFee: 30,
    description: 'AI visibility, answer-engine readiness, and citation opportunity audit - Monthly billing',
    features: [
      'AI visibility and citation readiness review',
      'Answer-engine source quality checks',
      'Quick overview score and priority metrics',
      'Owner-action recommendations',
      'Dashboard and email report path',
      'Pair with SEO & GBP Audit for $50/month total'
    ]
  },
  {
    id: SEO_GBP_AUDIT_OPTION_ID,
    name: 'SEO & GBP Audit - Monthly',
    serviceType: 'seo-gbp-audit',
    monthlyFee: 30,
    description: 'Website SEO, Google Business Profile, crawl, and local visibility audit - Monthly billing',
    features: [
      'Website SEO and crawl health review',
      'Google Business Profile readiness checks',
      'Search Console and GA4 reporting path',
      'Quick overview score and priority metrics',
      'Monthly change tracking path',
      'Pair with AI Audit for $50/month total'
    ]
  },
  {
    id: 'customer-intelligence-monthly',
    name: 'Customer Intelligence - Monthly',
    serviceType: 'customer-intelligence',
    monthlyFee: 299,
    description: 'Connected customer data intelligence, network leads, and attribution - Monthly billing',
    features: [
      'Requires active Repeat & Referral',
      'Customer opportunity scoring',
      'Rebook, referral, churn, and upsell signals',
      'Network lead review workflow',
      'Revenue attribution reporting',
      'R&R handoff with approval gates',
      'Best for businesses with usable customer or job history'
    ]
  },
  {
    id: 'customer-intelligence-annual',
    name: 'Customer Intelligence - Annual',
    serviceType: 'customer-intelligence',
    monthlyFee: 249,
    description: 'Connected customer data intelligence - Annual billing (2 months free)',
    features: [
      'Requires active Repeat & Referral',
      'Customer opportunity scoring',
      'Rebook, referral, churn, and upsell signals',
      'Network lead review workflow',
      'Revenue attribution reporting',
      'R&R handoff with approval gates',
      'Save $600/year with annual billing'
    ]
  }
];

function hydrateSelectedOptions(
  selectedServices: ReturnType<typeof useOnboardingStore.getState>['selectedServices'],
  selectedServiceTypes: ReturnType<typeof useOnboardingStore.getState>['selectedServiceTypes']
) {
  if (selectedServices && selectedServices.length > 0) {
    return selectedServices.map(service => {
      const fullOption = SERVICE_OPTIONS.find(opt => opt.id === service.id);
      return fullOption || {
        id: service.id,
        name: service.name,
        serviceType: '',
        monthlyFee: service.monthlyFee,
        initialCost: service.initialCost,
        description: service.description,
        features: []
      };
    });
  }

  if (isDeveloperMode && selectedServiceTypes.length > 0) {
    const defaultOption = SERVICE_OPTIONS.find(opt =>
      selectedServiceTypes.some(type => type.id === opt.serviceType)
    );
    return defaultOption ? [defaultOption] : [];
  }

  return [];
}

function optionIsAnnual(option: ServiceOption) {
  return option.id.includes('-annual');
}

function sameSelection(left: ServiceOption[], right: ServiceOption[]) {
  if (left.length !== right.length) return false;
  return left.every((option, index) => option.id === right[index]?.id);
}

function selectedHasService(options: ServiceOption[], serviceType: string) {
  return options.some(option => option.serviceType === serviceType);
}

function repeatReferralOptionFor(option: ServiceOption) {
  const wantsAnnual = optionIsAnnual(option);
  return SERVICE_OPTIONS.find(candidate =>
    candidate.serviceType === REPEAT_REFERRAL_SERVICE_TYPE &&
    optionIsAnnual(candidate) === wantsAnnual
  ) || SERVICE_OPTIONS.find(candidate => candidate.serviceType === REPEAT_REFERRAL_SERVICE_TYPE);
}

function normalizeCustomerIntelligenceDependency(
  options: ServiceOption[],
  availabilityRows: ServiceSubscriptionAvailability[]
) {
  const hasCustomerIntelligence = selectedHasService(options, CUSTOMER_INTELLIGENCE_SERVICE_TYPE);
  if (!hasCustomerIntelligence) return options;

  const repeatReferralUnavailable = serviceIsUnavailable(availabilityRows, REPEAT_REFERRAL_SERVICE_TYPE);
  if (repeatReferralUnavailable) {
    return options.filter(option => option.serviceType !== CUSTOMER_INTELLIGENCE_SERVICE_TYPE);
  }

  if (selectedHasService(options, REPEAT_REFERRAL_SERVICE_TYPE)) return options;

  const customerIntelligenceOption = options.find(option => option.serviceType === CUSTOMER_INTELLIGENCE_SERVICE_TYPE);
  const repeatReferralOption = customerIntelligenceOption ? repeatReferralOptionFor(customerIntelligenceOption) : undefined;
  return repeatReferralOption ? [repeatReferralOption, ...options] : options;
}

export function ServiceSelection() {
  const {
    setSelectedTier,
    selectedServiceTypes,
    setStep,
    setSelectedServices,
    selectedServices,
    client,
    textAvailabilityNotificationsAllowed,
    setTextAvailabilityNotificationsAllowed,
  } = useOnboardingStore();

  const [selectedOptions, setSelectedOptions] = useState<ServiceOption[]>(() =>
    hydrateSelectedOptions(selectedServices, selectedServiceTypes)
  );
  const [capacityRows, setCapacityRows] = useState<ServiceCapacity[]>([]);
  const [availabilityRows, setAvailabilityRows] = useState<ServiceSubscriptionAvailability[]>([]);
  const [capacityState, setCapacityState] = useState<LoadState>(() => (
    supabaseConfig.isConfigured ? 'loading' : 'error'
  ));
  const [availabilityState, setAvailabilityState] = useState<LoadState>('loading');
  const [selectionError, setSelectionError] = useState('');
  const [notifyRequested, setNotifyRequested] = useState<Record<string, boolean>>({});
  const [notifyBusy, setNotifyBusy] = useState<string | null>(null);

  const capacityFor = useCallback((serviceType: string) => {
    const slug = CAPACITY_SERVICE_SLUGS[serviceType];
    return slug ? capacityRows.find(row => row.service_slug === slug) : undefined;
  }, [capacityRows]);

  const capacityIsUnknown = useCallback((serviceType: string) => (
    Boolean(CAPACITY_SERVICE_SLUGS[serviceType]) && capacityState !== 'ready'
  ), [capacityState]);

  const optionAcceptsNewSubscriptions = useCallback((option: ServiceOption) => {
    if (availabilityState !== 'ready' || capacityIsUnknown(option.serviceType)) return false;
    const capacity = capacityFor(option.serviceType);
    const atCapacity = capacity?.limit_enabled === true && capacity?.is_at_capacity === true;
    return !serviceIsUnavailable(availabilityRows, option.serviceType) && !atCapacity;
  }, [availabilityRows, availabilityState, capacityFor, capacityIsUnknown]);

  useEffect(() => {
    let active = true;
    if (!supabaseConfig.isConfigured) {
      return () => { active = false; };
    }
    supabase.rpc('get_public_service_capacity_status')
      .then(({ data, error }) => {
        if (error) throw error;
        if (active) {
          setCapacityRows((data || []) as ServiceCapacity[]);
          setCapacityState('ready');
        }
      })
      .catch(() => {
        if (active) {
          setCapacityRows([]);
          setCapacityState('error');
        }
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    loadServiceSubscriptionAvailability()
      .then((rows) => {
        if (active) {
          setAvailabilityRows(rows);
          setAvailabilityState('ready');
        }
      })
      .catch(() => {
        if (active) {
          setAvailabilityRows([]);
          setAvailabilityState('error');
        }
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (availabilityState !== 'ready' || selectedOptions.length === 0) return;
    const filtered = normalizeCustomerIntelligenceDependency(
      selectedOptions.filter((option) => optionAcceptsNewSubscriptions(option)),
      availabilityRows
    );
    if (!sameSelection(filtered, selectedOptions)) {
      const timeoutId = window.setTimeout(() => {
        setSelectedOptions(filtered);
        setSelectedServices(filtered.map(opt => ({
          id: opt.id,
          name: opt.name,
          monthlyFee: opt.monthlyFee,
          initialCost: opt.initialCost,
          description: opt.description
        })));
      }, 0);
      return () => window.clearTimeout(timeoutId);
    }
  }, [availabilityRows, availabilityState, optionAcceptsNewSubscriptions, selectedOptions, setSelectedServices]);

  const handleSelectOption = (option: ServiceOption) => {
    setSelectionError('');
    if (availabilityState !== 'ready' || capacityIsUnknown(option.serviceType)) {
      setSelectionError('Service availability could not be verified. Refresh the page before selecting a plan.');
      return;
    }
    const capacity = capacityFor(option.serviceType);
    const atCapacity = capacity?.limit_enabled === true && capacity?.is_at_capacity === true;
    if (serviceIsUnavailable(availabilityRows, option.serviceType) || atCapacity) {
      void handleUnavailableOption(option);
      return;
    }

    setSelectedOptions(prev => {
      const isSelected = prev.find(opt => opt.id === option.id);

      if (isSelected) {
        const withoutOption = prev.filter(opt => opt.id !== option.id);
        if (option.serviceType === REPEAT_REFERRAL_SERVICE_TYPE) {
          return withoutOption.filter(opt => opt.serviceType !== CUSTOMER_INTELLIGENCE_SERVICE_TYPE);
        }
        return normalizeCustomerIntelligenceDependency(withoutOption, availabilityRows);
      }

      const filteredOptions = prev.filter(opt => opt.serviceType !== option.serviceType);
      return normalizeCustomerIntelligenceDependency([...filteredOptions, option], availabilityRows);
    });
  };

  const handleUnavailableOption = async (option: ServiceOption) => {
    setNotifyBusy(option.serviceType);
    try {
      await recordServiceAvailabilityInterest({
        serviceTypeId: option.serviceType,
        serviceName: option.name,
        email: client.email,
        phone: client.phone,
        contactName: client.contactName,
        businessName: client.businessName,
        textNotificationsAllowed: textAvailabilityNotificationsAllowed,
      });
      setNotifyRequested(prev => ({ ...prev, [option.serviceType]: true }));
    } catch {
      setNotifyRequested(prev => ({ ...prev, [option.serviceType]: false }));
      setSelectionError('The notification request could not be saved. Please try again.');
    } finally {
      setNotifyBusy(null);
    }
  };

  const handleContinue = () => {
    if (availabilityState !== 'ready') {
      setSelectionError('Service availability could not be verified. Refresh the page before continuing.');
      return;
    }
    const normalizedOptions = normalizeCustomerIntelligenceDependency(
      selectedOptions.filter((option) => optionAcceptsNewSubscriptions(option)),
      availabilityRows,
    );
    if (!sameSelection(normalizedOptions, selectedOptions)) {
      setSelectedOptions(normalizedOptions);
    }

    if (normalizedOptions.length === 0) return;

    if (normalizedOptions.length > 0) {
      // Store first selected option as the tier for compatibility
      const firstOption = normalizedOptions[0];
      setSelectedTier({
        id: firstOption.id,
        name: firstOption.name,
        price: firstOption.monthlyFee,
        priceId: firstOption.id,
        description: firstOption.description,
        features: firstOption.features,
        requiredIntegrations: []
      });

      // Store ALL selected services for cost calculation
      const servicesToStore = normalizedOptions.map(opt => ({
        id: opt.id,
        name: opt.name,
        monthlyFee: opt.monthlyFee,
        initialCost: opt.initialCost,
        description: opt.description
      }));

      setSelectedServices(servicesToStore);
    }
    setStep(4);
  };

  const handleBack = () => {
    setStep(2);
  };

  const handleSkip = () => {
    if (canSkipSteps()) {
      if (selectedOptions.length === 0) {
        const firstOption = SERVICE_OPTIONS.find(opt =>
          selectedServiceTypes.some(type => type.id === opt.serviceType) &&
          !serviceIsUnavailable(availabilityRows, opt.serviceType)
        );
        if (firstOption) {
          const normalizedOptions = normalizeCustomerIntelligenceDependency([firstOption], availabilityRows);
          if (normalizedOptions.length > 0) {
            const firstNormalizedOption = normalizedOptions[0];
            setSelectedOptions(normalizedOptions);
            setSelectedServices(normalizedOptions.map(opt => ({
              id: opt.id,
              name: opt.name,
              monthlyFee: opt.monthlyFee,
              initialCost: opt.initialCost,
              description: opt.description
            })));
            setSelectedTier({
              id: firstNormalizedOption.id,
              name: firstNormalizedOption.name,
              price: firstNormalizedOption.monthlyFee,
              priceId: firstNormalizedOption.id,
              description: firstNormalizedOption.description,
              features: firstNormalizedOption.features,
              requiredIntegrations: []
            });
          }
        }
      }
      setStep(4);
    }
  };

  // Filter options based on selected service types
  const availableOptions = SERVICE_OPTIONS.filter(option =>
    selectedServiceTypes.some(type => type.id === option.serviceType)
  );

  // Check if continue button should be enabled
  const hasCustomerIntelligenceDependencyIssue =
    selectedHasService(selectedOptions, CUSTOMER_INTELLIGENCE_SERVICE_TYPE) &&
    !selectedHasService(selectedOptions, REPEAT_REFERRAL_SERVICE_TYPE);
  const selectionsVerified = selectedOptions.every((option) => optionAcceptsNewSubscriptions(option));
  const isContinueEnabled = selectedOptions.length > 0
    && !hasCustomerIntelligenceDependencyIssue
    && selectionsVerified;

  // Calculate total costs
  const pairedAuditSelected =
    selectedOptions.some(opt => opt.id === AI_AUDIT_OPTION_ID) &&
    selectedOptions.some(opt => opt.id === SEO_GBP_AUDIT_OPTION_ID);
  const pairedAuditDiscount = pairedAuditSelected ? PAIRED_AUDIT_DISCOUNT : 0;
  const totalMonthlyEquivalent = Math.max(0, selectedOptions.reduce(
    (sum, opt) => sum + opt.monthlyFee,
    0,
  ) - pairedAuditDiscount);

  return (
    <div>
      <div className="text-center mb-8">
        <h2 className="text-2xl font-semibold text-white mb-2">
          Choose Your Service Options
        </h2>
        <p className="text-gray-400">
          Select the services that match your business needs and budget
        </p>
      </div>

      {(availabilityState === 'error' || capacityState === 'error' || selectionError) && (
        <div className="mb-6 rounded-lg border border-red-500/50 bg-red-500/10 px-4 py-3 text-sm text-red-200" role="alert">
          {selectionError || 'Service availability could not be verified. Refresh the page before selecting or continuing.'}
        </div>
      )}

      {client.phone && (
        <label className="mb-6 flex items-start gap-3 rounded-lg border border-gray-700 bg-scale-darker/40 p-4 text-left">
          <input
            type="checkbox"
            checked={textAvailabilityNotificationsAllowed}
            onChange={(event) => setTextAvailabilityNotificationsAllowed(event.target.checked)}
            className="mt-1 h-4 w-4 rounded border-gray-500 bg-scale-darker text-scale-blue focus:ring-scale-blue"
          />
          <span className="text-sm leading-6 text-gray-300">
            Text me at {client.phone} when a service I request becomes available. Message and data rates may apply. You can opt out at any time.
          </span>
        </label>
      )}

      {/* Service Options Grid - Paired rows: Monthly | Annual */}
      <div className="mb-8 space-y-6">
        {selectedServiceTypes.map((serviceType) => {
          const monthlyOption = availableOptions.find(
            opt => opt.serviceType === serviceType.id && !opt.id.includes('-annual')
          );
          const annualOption = availableOptions.find(
            opt => opt.serviceType === serviceType.id && opt.id.includes('-annual')
          );

          if (!monthlyOption) return null;

          const isDeferredTierSelection = monthlyOption.id === 'local-authority-engine';
          const isMonthlySelected = selectedOptions.find(opt => opt.id === monthlyOption.id);
          const isAnnualSelected = annualOption ? selectedOptions.find(opt => opt.id === annualOption.id) : false;
          const serviceCapacity = capacityFor(serviceType.id);
          const atCapacity = serviceCapacity?.limit_enabled && serviceCapacity?.is_at_capacity;
          const unavailable = serviceIsUnavailable(availabilityRows, serviceType.id);
          const availabilityUnknown = availabilityState !== 'ready' || capacityIsUnknown(serviceType.id);
          const unavailableNotice = availabilityUnknown
            ? 'Availability is temporarily unavailable. Refresh before selecting this service.'
            : atCapacity
            ? CAPACITY_MESSAGE
            : serviceUnavailableMessage(availabilityRows, serviceType.id);
          const pairedAuditPartnerName = monthlyOption.id === AI_AUDIT_OPTION_ID
            ? 'SEO & GBP Audit'
            : monthlyOption.id === SEO_GBP_AUDIT_OPTION_ID
            ? 'AI Audit'
            : null;
          const isCustomerIntelligence = serviceType.id === CUSTOMER_INTELLIGENCE_SERVICE_TYPE;
          const notifyLabel = notifyRequested[serviceType.id]
            ? 'Notification requested'
            : notifyBusy === serviceType.id
            ? 'Saving...'
            : 'Notify me when available';

          return (
            <div key={serviceType.id}>
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                {serviceType.name}
              </h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {/* Monthly Card */}
                <button
                  type="button"
                  onClick={() => handleSelectOption(monthlyOption)}
                  disabled={availabilityUnknown || notifyBusy === serviceType.id}
                  aria-pressed={Boolean(isMonthlySelected)}
                  className={`relative flex flex-col border rounded-lg p-6 text-left bg-scale-darker bg-opacity-70 backdrop-blur-sm transition-all duration-300 group ${
                    unavailable || availabilityUnknown
                      ? 'border-gray-800 opacity-60 hover:border-gray-600'
                      : atCapacity
                      ? 'border-green-500/70 hover:border-green-400'
                      : isMonthlySelected
                      ? 'border-scale-blue ring-2 ring-scale-blue ring-opacity-50'
                      : isAnnualSelected
                      ? 'border-yellow-600 hover:border-yellow-500'
                      : 'border-gray-700 hover:border-scale-blue'
                  }`}
                >
                  <div className="absolute -top-3 -right-3">
                    <Check className={`w-6 h-6 ${
                      unavailable || availabilityUnknown
                        ? 'text-gray-600 opacity-0'
                        : isMonthlySelected
                        ? 'text-scale-blue'
                        : 'text-scale-blue opacity-0 group-hover:opacity-100'
                    } transition-opacity`} />
                  </div>

                  {!unavailable && !availabilityUnknown && isAnnualSelected && !isMonthlySelected && (
                    <div className="mb-2 px-2 py-1 bg-yellow-600/20 border border-yellow-600/50 rounded text-xs text-yellow-400">
                      Will replace: Annual billing
                    </div>
                  )}

                  <h4 className="text-lg font-semibold text-white mb-3">{monthlyOption.name}</h4>

                  <div className="mb-3">
                    <p className="text-2xl font-bold text-scale-blue">
                      {isDeferredTierSelection ? `Starts at $${monthlyOption.monthlyFee}` : `$${monthlyOption.monthlyFee}`}
                      <span className="text-sm text-white font-normal">/month</span>
                    </p>
                  </div>

                  <p className="text-gray-300 mb-4 text-sm">{monthlyOption.description}</p>
                  {pairedAuditPartnerName && !unavailable && !availabilityUnknown && (
                    <div className="mb-4 px-3 py-2 bg-scale-blue/10 border border-scale-blue/30 rounded text-xs text-scale-blue">
                      Pair with {pairedAuditPartnerName} for ${PAIRED_AUDIT_PRICE}/month total. Select both audit cards and the $10/month paired audit price adjustment is applied automatically.
                    </div>
                  )}
                  {isCustomerIntelligence && !unavailable && !availabilityUnknown && (
                    <div className="mb-4 px-3 py-2 bg-yellow-600/20 border border-yellow-600/50 rounded text-xs text-yellow-300">
                      Requires active Repeat & Referral. Selecting Customer Intelligence also adds Repeat & Referral and includes it in your total.
                    </div>
                  )}
                  {(unavailable || atCapacity || availabilityUnknown) && (
                    <div className="mb-4 rounded-md border border-gray-700 bg-black/20 px-3 py-2 text-xs text-gray-300">
                      {unavailableNotice}
                    </div>
                  )}
                  <CapacityMeter capacity={serviceCapacity} />
                  {isDeferredTierSelection && (
                    <div className="mb-4 px-3 py-2 bg-scale-blue/10 border border-scale-blue/30 rounded text-xs text-scale-blue">
                      Note: you will choose Foundation, Boost, Pro, or Market Leader in the dashboard before Stripe payment starts.
                    </div>
                  )}

                  <ul className="space-y-2 mb-6 flex-grow">
                    {monthlyOption.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <Check className="w-4 h-4 text-scale-blue mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-white">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <div
                    className={`w-full font-semibold px-4 py-3 rounded-md transition-colors mt-auto text-center ${
                      isMonthlySelected
                        ? 'bg-white text-scale-dark'
                        : unavailable || atCapacity || availabilityUnknown
                        ? 'bg-gray-700/30 text-gray-300'
                        : 'bg-scale-blue text-scale-dark hover:bg-white'
                    }`}
                  >
                    {availabilityUnknown ? 'Availability unavailable' : unavailable || atCapacity ? notifyLabel : isMonthlySelected ? 'Selected' : isDeferredTierSelection ? 'Select Service' : 'Select Monthly'}
                  </div>
                </button>

                {/* Annual Card OR Locked Placeholder */}
                {annualOption ? (
                  <button
                    type="button"
                    onClick={() => handleSelectOption(annualOption)}
                    disabled={availabilityUnknown || notifyBusy === serviceType.id}
                    aria-pressed={Boolean(isAnnualSelected)}
                    className={`relative flex flex-col border rounded-lg p-6 text-left bg-scale-darker bg-opacity-70 backdrop-blur-sm transition-all duration-300 group ${
                        unavailable || availabilityUnknown
                          ? 'border-gray-800 opacity-60 hover:border-gray-600'
                          : atCapacity
                          ? 'border-green-500/70 hover:border-green-400'
                          : isAnnualSelected
                          ? 'border-scale-blue ring-2 ring-scale-blue ring-opacity-50'
                          : isMonthlySelected
                          ? 'border-yellow-600 hover:border-yellow-500'
                        : 'border-gray-700 hover:border-scale-blue'
                    }`}
                  >
                    <div className="absolute -top-3 -right-3">
                      <Check className={`w-6 h-6 ${
                      unavailable || availabilityUnknown
                          ? 'text-gray-600 opacity-0'
                          : isAnnualSelected
                          ? 'text-scale-blue'
                          : 'text-scale-blue opacity-0 group-hover:opacity-100'
                      } transition-opacity`} />
                    </div>

                    {!unavailable && !availabilityUnknown && isMonthlySelected && !isAnnualSelected && (
                      <div className="mb-2 px-2 py-1 bg-yellow-600/20 border border-yellow-600/50 rounded text-xs text-yellow-400">
                        Will replace: Monthly billing
                      </div>
                    )}

                    <h4 className="text-lg font-semibold text-white mb-3">{annualOption.name}</h4>

                    <div className="mb-3">
                      <p className="text-2xl font-bold text-scale-blue">
                        ${annualOption.monthlyFee}
                        <span className="text-sm text-white font-normal">/month</span>
                      </p>
                    </div>

                    <p className="text-gray-300 mb-4 text-sm">{annualOption.description}</p>
                    {(unavailable || atCapacity || availabilityUnknown) && (
                      <div className="mb-4 rounded-md border border-gray-700 bg-black/20 px-3 py-2 text-xs text-gray-300">
                        {unavailableNotice}
                      </div>
                    )}
                    {isCustomerIntelligence && !unavailable && !availabilityUnknown && (
                      <div className="mb-4 px-3 py-2 bg-yellow-600/20 border border-yellow-600/50 rounded text-xs text-yellow-300">
                        Requires active Repeat & Referral. Selecting Customer Intelligence also adds Repeat & Referral and includes it in your total.
                      </div>
                    )}
                    <CapacityMeter capacity={serviceCapacity} />

                    <ul className="space-y-2 mb-6 flex-grow">
                      {annualOption.features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <Check className="w-4 h-4 text-scale-blue mr-2 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-white">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <div
                      className={`w-full font-semibold px-4 py-3 rounded-md transition-colors mt-auto text-center ${
                        isAnnualSelected
                          ? 'bg-white text-scale-dark'
                          : unavailable || atCapacity || availabilityUnknown
                          ? 'bg-gray-700/30 text-gray-300'
                          : 'bg-scale-blue text-scale-dark hover:bg-white'
                      }`}
                    >
                      {availabilityUnknown ? 'Availability unavailable' : unavailable || atCapacity ? notifyLabel : isAnnualSelected ? 'Selected' : 'Select Annual'}
                    </div>
                  </button>
                ) : (
                  <div className="relative flex flex-col border border-gray-700/50 rounded-lg p-6 bg-scale-darker/30 backdrop-blur-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <Lock className="w-5 h-5 text-gray-500" />
                      <h4 className="text-lg font-semibold text-gray-500">
                        {isDeferredTierSelection ? 'Tier Selection' : 'Annual Plan'}
                      </h4>
                    </div>
                    <div className="flex-grow flex flex-col items-center justify-center text-center py-8">
                      <Lock className="w-10 h-10 text-gray-600 mb-4" />
                      <p className="text-gray-500 text-sm font-medium">
                        {isDeferredTierSelection ? 'Selected later before payment' : 'No annual discount available'}
                      </p>
                      <p className="text-gray-600 text-xs mt-2">
                        {isDeferredTierSelection
                          ? 'Tier and annual billing choices are handled in the dashboard.'
                          : 'This service is offered at a flat monthly rate'}
                      </p>
                    </div>
                    <div className="w-full font-semibold px-4 py-3 rounded-md mt-auto text-center bg-gray-700/30 text-gray-500 cursor-not-allowed">
                      {isDeferredTierSelection ? 'Dashboard Step' : 'Not Available'}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Cost Summary */}
      {selectedOptions.length > 0 && (
        <div className="bg-scale-darker/50 border border-gray-700 rounded-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-white mb-4">Cost Summary</h3>

          <div className="space-y-3">
            {selectedOptions.map((option) => {
              const isAnnual = option.id.includes('-annual');
              const annualTotal = isAnnual ? option.monthlyFee * 12 : null;

              return (
                <div key={option.id} className="flex justify-between text-sm">
                  <span className="text-gray-300">{option.name}</span>
                  <div className="text-right">
                    <div className="text-white">
                      {option.id === 'local-authority-engine' ? `Starts at $${option.monthlyFee}/month` : `$${option.monthlyFee}/month`}
                    </div>
                    {isAnnual && annualTotal && (
                      <div className="text-scale-blue text-xs mt-0.5">
                        ${annualTotal}/year billed annually
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {pairedAuditSelected && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-300">AI Audit + SEO & GBP Audit paired audit price adjustment</span>
                <div className="text-right text-scale-blue">-$10/month</div>
              </div>
            )}
            {selectedHasService(selectedOptions, CUSTOMER_INTELLIGENCE_SERVICE_TYPE) && (
              <div className="rounded-md border border-yellow-600/50 bg-yellow-600/10 px-3 py-2 text-xs leading-relaxed text-yellow-300">
                {hasCustomerIntelligenceDependencyIssue
                  ? 'Customer Intelligence requires active Repeat & Referral. Select Repeat & Referral before continuing.'
                  : 'Customer Intelligence requires active Repeat & Referral. Repeat & Referral is included in this selection and total.'}
              </div>
            )}

            <div className="border-t border-gray-700 pt-3 mt-4">
              <div className="flex justify-between font-bold">
                <span className="text-white">
                  {selectedOptions.some(opt => opt.id === 'local-authority-engine') ? 'Estimated Starting Monthly Equivalent' : 'Estimated Monthly Equivalent'}
                </span>
                <span className="text-scale-blue">
                  {selectedOptions.some(opt => opt.id === 'local-authority-engine') ? 'Starts at ' : ''}${totalMonthlyEquivalent}/month
                </span>
              </div>
              {selectedOptions.some(opt => opt.id === 'local-authority-engine') && (
                <div className="text-xs text-gray-400 mt-2">
                  Local Authority tier level and monthly/annual billing are selected later in the dashboard before Stripe payment starts.
                </div>
              )}
              {selectedOptions.some(opt => opt.id.includes('-annual')) && (
                <div className="flex justify-between font-bold mt-2">
                  <span className="text-white">Total Annual Cost</span>
                  <span className="text-scale-blue">
                    ${selectedOptions.reduce((sum, opt) => {
                      if (opt.id.includes('-annual')) {
                        return sum + (opt.monthlyFee * 12);
                      }
                      return sum;
                    }, 0)}/year
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center space-x-4 pt-4">
        <button
          onClick={handleBack}
          className="w-32 flex items-center justify-center px-6 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600 transition-colors font-semibold"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </button>
        <div className="flex gap-3">
          {canSkipSteps() && (
            <button
              onClick={handleSkip}
              className="flex items-center bg-yellow-600 text-white px-4 py-2 rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 transition-colors font-medium"
            >
              <FastForward className="w-4 h-4 mr-2" />
              Skip (Beta)
            </button>
          )}
          <button
            onClick={handleContinue}
            disabled={!isContinueEnabled}
            className={`w-32 font-semibold px-6 py-2 rounded-md transition-colors ${
              isContinueEnabled
                ? 'bg-scale-blue text-scale-dark hover:bg-white'
                : 'bg-gray-600 text-gray-400 cursor-not-allowed'
            }`}
          >
            Continue
          </button>
        </div>
      </div>

      {selectedOptions.length > 0 && (
        <div className="mt-6 p-4 bg-scale-darker/50 border border-gray-700 rounded-lg">
          <h4 className="text-sm font-semibold text-white mb-2">Selected Services:</h4>
          <div className="flex flex-wrap gap-2">
            {selectedOptions.map((option) => (
              <div
                key={option.id}
                className="flex items-center bg-scale-blue/10 text-scale-blue px-3 py-1 rounded-full text-sm"
              >
                {option.name}
                <button
                  type="button"
                  aria-label={`Remove ${option.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectOption(option);
                  }}
                  className="ml-2 hover:text-white"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CapacityMeter({ capacity }: { capacity?: ServiceCapacity }) {
  if (!capacity?.limit_enabled) return null;
  const limit = Number(capacity.client_limit || 50);
  const atCapacity = capacity.is_at_capacity;

  return (
    <div className={`mb-4 rounded-md border p-3 ${atCapacity ? 'border-green-500/60 bg-green-500/10' : 'border-gray-700 bg-black/20'}`}>
      <div className="flex items-center justify-between text-xs font-semibold">
        <span className={atCapacity ? 'text-green-300' : 'text-gray-300'}>{capacity.service_name} availability</span>
        <span className={atCapacity ? 'text-green-300' : 'text-white'}>Limit: {limit} clients</span>
      </div>
      {atCapacity && (
        <p className="mt-2 text-xs leading-relaxed text-green-300">
          {CAPACITY_MESSAGE}
        </p>
      )}
    </div>
  );
}
